import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ImageOverlay, Pane } from 'react-leaflet';
import L from 'leaflet';
import {
  Box,
  GlobalStyles,
  IconButton,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

// Mirrors DTOs/SatelliteDtos.cs.
export interface SatelliteImageMeta {
  id: string;
  index: string;
  date: string;
  width: number;
  height: number;
  resolutionMetres: number;
  south: number;
  west: number;
  north: number;
  east: number;
  encodedMin: number;
  encodedMax: number;
  processingUnits: number | null;
}

export interface SatelliteImageList {
  images: SatelliteImageMeta[];
  estimatedProcessingUnits: number | null;
  resolutionMetres: number | null;
  monthProcessingUnits: number;
  monthlyBudget: number;
}

// Pixel codes in the image PNG — see SentinelHubClient's NdviImageEncoding.
const CODE_NO_DATA = 0;
const CODE_NOT_MEASURED = 1;
const CODE_FIRST_VALUE = 2;
const CODE_LAST_VALUE = 255;

// "fixed" puts every image on the same NDVI scale, so dates compare;
// "stretched" spreads the colours over this image's own 2nd–98th
// percentile, which shows differences inside a dry or uniform farm that
// the fixed scale flattens.
export type StretchMode = 'fixed' | 'stretched';
const FIXED_RANGE: [number, number] = [0, 0.9];
const MIN_STRETCH_SPAN = 0.05;

// ColorBrewer Greens: one hue, light (little vegetation) to dark (dense) —
// sequential, so lightness alone carries the order and it reads the same
// with red-green colour blindness, unlike the usual red-yellow-green.
const RAMP = ['#f7fcf5', '#e5f5e0', '#c7e9c0', '#a1d99b', '#74c476', '#41ab5d', '#238b45', '#006d2c', '#00441b'];
const RAMP_RGB = RAMP.map((hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)));
// Cloud, shadow, water: a see-through grey, distinct in lightness and
// chroma from every step of the ramp.
const NOT_MEASURED_RGBA = [120, 120, 120, 150];
export const NOT_MEASURED_CSS = `rgba(${NOT_MEASURED_RGBA.slice(0, 3).join(',')},${(NOT_MEASURED_RGBA[3] / 255).toFixed(2)})`;
export const RAMP_CSS = `linear-gradient(to right, ${RAMP.join(', ')})`;

export interface SatelliteOverlay {
  meta: SatelliteImageMeta;
  featureId: string;
  featureName: string;
  width: number;
  height: number;
  // One pixel code per pixel, row 0 = north.
  codes: Uint8Array;
  // 2nd–98th percentile of this image's measured NDVI.
  stretchRange: [number, number];
  stretch: StretchMode;
  visible: boolean;
  opacity: number;
  // The coloured image for the current stretch.
  objectUrl: string;
}

const codeToValue = (code: number, meta: SatelliteImageMeta) =>
  meta.encodedMin + ((code - CODE_FIRST_VALUE) / (CODE_LAST_VALUE - CODE_FIRST_VALUE)) * (meta.encodedMax - meta.encodedMin);

export const overlayRange = (o: SatelliteOverlay): [number, number] => (o.stretch === 'fixed' ? FIXED_RANGE : o.stretchRange);

const rampColour = (t: number): number[] => {
  const x = Math.min(1, Math.max(0, t)) * (RAMP_RGB.length - 1);
  const i = Math.min(RAMP_RGB.length - 2, Math.floor(x));
  const f = x - i;
  return RAMP_RGB[i].map((c, k) => Math.round(c + (RAMP_RGB[i + 1][k] - c) * f));
};

// Reads the PNG's grey level back as the pixel code, with no colour
// management or alpha premultiplication in between.
const decodeCodes = async (blob: Blob) => {
  const bitmap = await createImageBitmap(blob, { colorSpaceConversion: 'none', premultiplyAlpha: 'none' });
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas not available');
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const codes = new Uint8Array(canvas.width * canvas.height);
  for (let i = 0; i < codes.length; i++) codes[i] = data[i * 4];
  return { codes, width: canvas.width, height: canvas.height };
};

const percentileRange = (codes: Uint8Array, meta: SatelliteImageMeta): [number, number] => {
  const histogram = new Array<number>(256).fill(0);
  let total = 0;
  for (let i = 0; i < codes.length; i++) {
    if (codes[i] >= CODE_FIRST_VALUE) {
      histogram[codes[i]]++;
      total++;
    }
  }
  if (total === 0) return FIXED_RANGE;
  const at = (p: number) => {
    let seen = 0;
    for (let code = CODE_FIRST_VALUE; code <= CODE_LAST_VALUE; code++) {
      seen += histogram[code];
      if (seen >= p * total) return codeToValue(code, meta);
    }
    return meta.encodedMax;
  };
  let lo = at(0.02);
  let hi = at(0.98);
  if (hi - lo < MIN_STRETCH_SPAN) {
    const mid = (lo + hi) / 2;
    lo = mid - MIN_STRETCH_SPAN / 2;
    hi = mid + MIN_STRETCH_SPAN / 2;
  }
  return [lo, hi];
};

const colourise = async (codes: Uint8Array, width: number, height: number, meta: SatelliteImageMeta, range: [number, number]) => {
  const lut = new Uint8ClampedArray(256 * 4);
  for (let code = CODE_FIRST_VALUE; code <= CODE_LAST_VALUE; code++) {
    const [r, g, b] = rampColour((codeToValue(code, meta) - range[0]) / (range[1] - range[0]));
    lut.set([r, g, b, 255], code * 4);
  }
  lut.set(NOT_MEASURED_RGBA, CODE_NOT_MEASURED * 4);
  // CODE_NO_DATA stays transparent.

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not available');
  const image = ctx.createImageData(width, height);
  for (let i = 0; i < codes.length; i++) {
    image.data.set(lut.subarray(codes[i] * 4, codes[i] * 4 + 4), i * 4);
  }
  ctx.putImageData(image, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Could not render the image');
  return URL.createObjectURL(blob);
};

/**
 * NDVI images shown as map layers — fetched per feature and date by
 * SatellitePanel (POST .../satellite-images), then downloaded, decoded and
 * coloured here. Held for the session only; the server keeps the image, so
 * adding it again later is free.
 */
export const useSatelliteOverlays = (authHeader: Record<string, string>) => {
  const [overlays, setOverlays] = useState<SatelliteOverlay[]>([]);
  const overlaysRef = useRef(overlays);
  overlaysRef.current = overlays;

  useEffect(() => () => overlaysRef.current.forEach((o) => URL.revokeObjectURL(o.objectUrl)), []);

  // Adds the image as the top layer, or just shows it if it's already there.
  const addOverlay = useCallback(async (featureId: string, featureName: string, meta: SatelliteImageMeta) => {
    if (overlaysRef.current.some((o) => o.meta.id === meta.id)) {
      setOverlays((prev) => prev.map((o) => (o.meta.id === meta.id ? { ...o, visible: true } : o)));
      return;
    }
    const res = await fetch(`/api/spatial-features/${featureId}/satellite-images/${meta.id}`, { headers: authHeader });
    if (!res.ok) throw new Error(`Failed to download the image (${res.status})`);
    const { codes, width, height } = await decodeCodes(await res.blob());
    const stretchRange = percentileRange(codes, meta);
    const stretch: StretchMode = 'stretched';
    const objectUrl = await colourise(codes, width, height, meta, stretchRange);
    setOverlays((prev) => [
      ...prev.filter((o) => o.meta.id !== meta.id),
      { meta, featureId, featureName, width, height, codes, stretchRange, stretch, visible: true, opacity: 1, objectUrl },
    ]);
  }, [authHeader]);

  const removeOverlay = useCallback((id: string) => {
    setOverlays((prev) => {
      prev.filter((o) => o.meta.id === id).forEach((o) => URL.revokeObjectURL(o.objectUrl));
      return prev.filter((o) => o.meta.id !== id);
    });
  }, []);

  const updateOverlay = useCallback((id: string, patch: Partial<Pick<SatelliteOverlay, 'visible' | 'opacity'>>) => {
    setOverlays((prev) => prev.map((o) => (o.meta.id === id ? { ...o, ...patch } : o)));
  }, []);

  const setStretch = useCallback(async (id: string, stretch: StretchMode) => {
    const o = overlaysRef.current.find((x) => x.meta.id === id);
    if (!o || o.stretch === stretch) return;
    const objectUrl = await colourise(o.codes, o.width, o.height, o.meta, stretch === 'fixed' ? FIXED_RANGE : o.stretchRange);
    setOverlays((prev) => prev.map((x) => {
      if (x.meta.id !== id) return x;
      URL.revokeObjectURL(x.objectUrl);
      return { ...x, stretch, objectUrl };
    }));
  }, []);

  return { overlays, addOverlay, removeOverlay, updateOverlay, setStretch };
};

// What's under the cursor on the topmost visible image, e.g.
// "pem1 · 19 Sep 2026: NDVI 0.42" — or null if no image covers it.
export const readOverlayAt = (overlays: SatelliteOverlay[], latLng: { lat: number; lng: number }): string | null => {
  const crs = L.CRS.EPSG3857;
  for (let k = overlays.length - 1; k >= 0; k--) {
    const o = overlays[k];
    const { meta } = o;
    if (!o.visible || latLng.lat < meta.south || latLng.lat > meta.north || latLng.lng < meta.west || latLng.lng > meta.east) continue;
    // The image's pixel grid is even in Web Mercator, not in degrees.
    const p = crs.project(L.latLng(latLng.lat, latLng.lng));
    const sw = crs.project(L.latLng(meta.south, meta.west));
    const ne = crs.project(L.latLng(meta.north, meta.east));
    const x = Math.min(o.width - 1, Math.floor(((p.x - sw.x) / (ne.x - sw.x)) * o.width));
    const y = Math.min(o.height - 1, Math.floor(((ne.y - p.y) / (ne.y - sw.y)) * o.height));
    const code = o.codes[y * o.width + x];
    if (code === CODE_NO_DATA) continue;
    const label = `${o.featureName} · ${formatImageDate(meta.date)}`;
    return code === CODE_NOT_MEASURED
      ? `${label}: not measured (cloud, shadow or water)`
      : `${label}: ${meta.index} ${codeToValue(code, meta).toFixed(2)}`;
  }
  return null;
};

export const formatImageDate = (date: string) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

// Goes inside the MapContainer. Its own pane sits above the basemap but
// below feature outlines (overlayPane, z 400), so boundaries stay visible
// and clickable on top of the image.
export const SatelliteImageryLayer: React.FC<{ overlays: SatelliteOverlay[] }> = ({ overlays }) => (
  <>
    {/* Nearest-neighbour scaling, so each 10 m pixel shows as a square
        rather than being blurred into its neighbours. */}
    <GlobalStyles styles={{ '.dmotrak-satellite-image': { imageRendering: 'pixelated' } }} />
    <Pane name="satellite-imagery" style={{ zIndex: 350 }}>
      {overlays.filter((o) => o.visible).map((o) => (
        <ImageOverlay
          key={`${o.meta.id}-${o.objectUrl}`}
          url={o.objectUrl}
          bounds={[[o.meta.south, o.meta.west], [o.meta.north, o.meta.east]]}
          opacity={o.opacity}
          className="dmotrak-satellite-image"
        />
      ))}
    </Pane>
  </>
);

export const SatelliteLegend: React.FC<{ range: [number, number]; index: string; stretch: StretchMode }> = ({ range, index, stretch }) => (
  <Box>
    <Box sx={{ height: 10, borderRadius: 0.5, background: RAMP_CSS, border: '1px solid', borderColor: 'divider' }} />
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography variant="caption" color="text.secondary">{range[0].toFixed(2)}</Typography>
      <Typography variant="caption" color="text.secondary">
        {index}{stretch === 'stretched' ? ' · this image' : ''}
      </Typography>
      <Typography variant="caption" color="text.secondary">{range[1].toFixed(2)}</Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 12, height: 10, borderRadius: 0.5, bgcolor: NOT_MEASURED_CSS, border: '1px solid', borderColor: 'divider' }} />
      <Typography variant="caption" color="text.secondary">Not measured: cloud, shadow or water</Typography>
    </Box>
  </Box>
);

interface ControlsProps {
  overlays: SatelliteOverlay[];
  onUpdate: (id: string, patch: Partial<Pick<SatelliteOverlay, 'visible' | 'opacity'>>) => void;
  onStretch: (id: string, stretch: StretchMode) => void;
  onRemove: (id: string) => void;
}

// One row per image layer: show/hide, remove, colour scale, opacity, legend.
// Newest (topmost) first.
export const SatelliteImageryControls: React.FC<ControlsProps> = ({ overlays, onUpdate, onStretch, onRemove }) => (
  <Box>
    {[...overlays].reverse().map((o) => (
      <Box key={o.meta.id} sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => onUpdate(o.meta.id, { visible: !o.visible })}
            aria-label={o.visible ? 'Hide image' : 'Show image'}
          >
            {o.visible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
          </IconButton>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap>{o.featureName}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {o.meta.index} · {formatImageDate(o.meta.date)} · {Math.round(o.meta.resolutionMetres)} m pixels
            </Typography>
          </Box>
          <MuiTooltip title="Remove from map">
            <IconButton size="small" onClick={() => onRemove(o.meta.id)} aria-label="Remove image">
              <CloseIcon fontSize="small" />
            </IconButton>
          </MuiTooltip>
        </Box>
        {o.visible && (
          <Box sx={{ pl: 4.5, pr: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={o.stretch}
                onChange={(_, v) => v && onStretch(o.meta.id, v)}
                sx={{ '& .MuiToggleButton-root': { py: 0, px: 1, fontSize: 12, textTransform: 'none' } }}
              >
                {/* Plain title attributes: ToggleButtonGroup must wrap its
                    ToggleButtons directly, so no MUI Tooltip in between. */}
                <ToggleButton value="stretched" title="Colours spread over this image's own range: shows differences within the farm">
                  Stretched
                </ToggleButton>
                <ToggleButton value="fixed" title={`Same scale for every date (${FIXED_RANGE[0]}–${FIXED_RANGE[1]}): compare dates`}>
                  Fixed
                </ToggleButton>
              </ToggleButtonGroup>
              <Slider
                size="small"
                min={0.2}
                max={1}
                step={0.05}
                value={o.opacity}
                onChange={(_, v) => onUpdate(o.meta.id, { opacity: v as number })}
                aria-label="Opacity"
                sx={{ flexGrow: 1 }}
              />
            </Box>
            <SatelliteLegend range={overlayRange(o)} index={o.meta.index} stretch={o.stretch} />
          </Box>
        )}
      </Box>
    ))}
  </Box>
);
